import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { navigate, graphql } from 'gatsby'
import Layout from '../components/Layout'
import TalksHero from '../components/TalksHero'
import CourseColumns from '../components/CourseColumns'
import Sidebar from '../components/Sidebar'
import Seo from '../components/SEO'

// eslint-disable-next-line
export const TalksPageTemplate = ({
  talksDescription,
  selectedTalk,
  courses,
  titles,
  handleTalkClick,
}) => {
  return (
    <>
      <div className="top-spacer"></div>
      {talksDescription && (
        <TalksHero
          headline={talksDescription.headline}
          subheadline={talksDescription.subheadline}
          image={talksDescription.image}
          description={talksDescription.description}
          video_sv={talksDescription.video_sv.publicURL}
          video_en={talksDescription.video_en.publicURL}
        />
      )}
      <div className="course-container">
        <Sidebar
          courses={courses}
          titles={titles} // Pass titles to the Sidebar
          selectedCourse={selectedTalk}
          handleCourseClick={handleTalkClick}
        />
        <div id="course">
          {selectedTalk && (
            <>
              <CourseColumns
                image={selectedTalk.image}
                preheadline={selectedTalk.preheadline}
                headline={selectedTalk.headline}
                text={selectedTalk.text}
                quote={selectedTalk.quote}
                quoteName={selectedTalk.quoteName}
                ctaButtonUrl={selectedTalk.ctaButtonUrl}
                ctaButton={selectedTalk.ctaButton}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}

TalksPageTemplate.propTypes = {
  talksDescription: PropTypes.shape({
    headline: PropTypes.string,
    subheadline: PropTypes.string,
    image: PropTypes.object,
    description: PropTypes.string,
    video_sv: PropTypes.string,
    video_en: PropTypes.string,
  }),
  selectedTalk: PropTypes.shape({
    image: PropTypes.object,
    preheadline: PropTypes.string,
    headline: PropTypes.string,
    text: PropTypes.string,
    quote: PropTypes.string,
    quoteName: PropTypes.string,
    ctaButtonUrl: PropTypes.string,
    ctaButton: PropTypes.string,
  }),
  courses: PropTypes.arrayOf(PropTypes.string),
  titles: PropTypes.arrayOf(PropTypes.string),
  handleTalkClick: PropTypes.func,
}

// eslint-disable-next-line
const Talks = ({ data, location }) => {

  console.log('data======>',data)
  const courses = data.allMarkdownRemark.nodes.map(
    (node) => node.frontmatter.course
  )
  const titles = data.allMarkdownRemark.nodes.map(
    (node) => node.frontmatter.title
  )
  const talksDescription = data.markdownRemark.frontmatter.talksDescription

  const [selectedTalk, setSelectedTalk] = useState(null)
  const normalizeString = (str) => {
    return str
      .replace(/å/g, 'a')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .toLowerCase()
  }

  useEffect(() => {
    const slug = location.pathname.split('/')[2]
    const normalizedSlug = normalizeString(slug)

    const selectedNode = data.allMarkdownRemark.nodes.find((node) =>
      normalizeString(node.fields.slug).includes(normalizedSlug)
    )

    if (selectedNode) {
      setSelectedTalk(selectedNode.frontmatter.course)
    }

    setTimeout(() => {
      const anchorElement = document.getElementById('course')
      if (anchorElement && location.hash === '#course') {
        const navbarElement = document.querySelector('.custom-navbar')
        const sidebarElement = document.querySelector('.sidebar')
        const navbarHeight = navbarElement ? navbarElement.offsetHeight : 0
        const sidebarHeight = sidebarElement ? sidebarElement.offsetHeight : 0
        const scrollOffset =
          anchorElement.offsetTop -
          navbarHeight -
          (window.innerWidth >= 1024 ? 30 : sidebarHeight)
        window.scrollTo({ top: scrollOffset, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' }) // Scroll to top when #course is not present
      }
    }, 100)
  }, [data, location.pathname, location.hash])

  const handleTalkClick = (talk) => {
    if (talk !== selectedTalk) {
      setSelectedTalk(talk)
      const selectedNode = data.allMarkdownRemark.nodes.find(
        (node) => node.frontmatter.course === talk
      )
      if (selectedNode) {
        const actualPath = selectedNode.fields.slug
        navigate(`${actualPath.replace(/\/$/, '')}#course`)
      }
    }
  }

  return (
    <Layout>
      <>
        <div className="top-spacer"></div>
        {talksDescription && (
          <TalksHero
            headline={talksDescription.headline}
            subheadline={talksDescription.subheadline}
            image={talksDescription.image}
            description={talksDescription.description}
            video_sv={talksDescription.video_sv ? talksDescription.video_sv.publicURL : null}
            video_en={talksDescription.video_en ? talksDescription.video_en.publicURL : null}
          />
        )}
        <div className="course-container">
          <Sidebar
            courses={courses}
            titles={titles} // Pass titles to the Sidebar
            selectedCourse={selectedTalk}
            handleCourseClick={handleTalkClick}
          />
          <div id="course">
            {selectedTalk && (
              <>
                <Seo
                  pageDescription={selectedTalk.metadata.metaDescription}
                  pageTitle={selectedTalk.metadata.metaTitle}
                  ogImage={
                    selectedTalk.metadata.ogImage &&
                    selectedTalk.metadata.ogImage.publicURL
                      ? selectedTalk.metadata.ogImage.publicURL
                      : undefined
                  }
                  courses={courses}
                  url={location.pathname}
                />

                <CourseColumns
                  image={selectedTalk.image}
                  preheadline={selectedTalk.preheadline}
                  headline={selectedTalk.headline}
                  text={selectedTalk.text}
                  quote={selectedTalk.quote}
                  quoteName={selectedTalk.quoteName}
                  ctaButtonUrl={selectedTalk.ctaButtonUrl}
                  ctaButton={selectedTalk.ctaButton}
                />
              </>
            )}
          </div>
        </div>
      </>
    </Layout>
  )
}

Talks.propTypes = {
  data: PropTypes.shape({
    allMarkdownRemark: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          frontmatter: PropTypes.shape({
            course: PropTypes.shape({
              // Define your PropTypes here
            }),
          }),
          fields: PropTypes.shape({
            slug: PropTypes.string,
          }),
        })
      ),
    }),
  }),
  location: PropTypes.object.isRequired,
}

export default Talks

export const talksQuery = graphql`
  query Talks {
    allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "talks" } } }
    ) {
      nodes {
        frontmatter {
          title
          course {
            metadata {
              metaTitle
              metaDescription
              ogImage {
                childImageSharp {
                  gatsbyImageData(layout: CONSTRAINED, height: 1200, width: 630)
                }
              }
            }
            image {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED, aspectRatio: 0.8)
              }
            }
            preheadline
            headline
            text
            quote {
              text
              name
            }
            quoteName
            ctaButtonUrl
            ctaButton
          }
        }
        fields {
          slug
        }
      }
    }
    markdownRemark(frontmatter: { title: { eq: "talks description" } }) {
      id
      frontmatter {
        talksDescription {
          description
          image {
            childImageSharp {
              gatsbyImageData(
                layout: FULL_WIDTH
                height: 500
                transformOptions: { cropFocus: ENTROPY, fit: COVER }
              )
            }
          }
          headline
          subheadline
          video_sv {
            publicURL
          }
          video_en {
            publicURL
          }
        }
      }
    }
  }
`
